import type { ReadinessFactor, ReadinessGoal, ReadinessResult } from "../../../../lib/readiness-types";
import { factorsForRow, goalsForRow, type ProfileRowId } from "../../../../lib/readiness/factor-routing";
import type { SharpenPillVariant } from "../../../readiness/sharpen-pill";

export interface SharpenMeta {
  factor: ReadinessFactor;
  goal: ReadinessGoal;
  // variant tells the renderer which chip shape to use:
  //   "delta"    — default; "+N pts" pill for scored users.
  //   "unlocks"  — cap-resolving goal (goal.unlocks_segment set).
  //   "set-plan" — goal.kind === "set_plan" (Option C(b)); state-change
  //                prompt, no points reward.
  //   "none"     — cap-shadowed non-cap goal; action label only.
  variant: SharpenPillVariant;
}

// Returns the sharpen-pill data for a row, or null if no meta should render.
//
// A row's sharpen meta only appears when its readiness factor exists, has
// room to grow (value < max), and at least one goal targets it. For
// Option C(b) the "set-plan" variant is allowed even at full factor max
// because it's a state-change prompt, not a points-raising move.
export function sharpenMetaForRow(
  rowId: ProfileRowId,
  readiness: ReadinessResult | null | undefined,
): SharpenMeta | null {
  const factor = factorsForRow(rowId, readiness);
  const goals = goalsForRow(rowId, readiness);
  if (!factor || goals.length === 0) {
    return null;
  }
  const goal = goals[0];
  const isCapped = readiness?.cap_reason && readiness.cap_reason !== "none";
  let variant: SharpenPillVariant;
  if (goal.kind === "set_plan") {
    variant = "set-plan";
  } else if (isCapped) {
    variant = goal.unlocks_segment ? "unlocks" : "none";
  } else if (factor.value >= factor.max) {
    // Factor maxed and not a cap-or-set-plan goal — no pill to show.
    return null;
  } else {
    variant = "delta";
  }
  return { factor, goal, variant };
}
